import { Upload } from "antd";

export type UploadValidationOptions = {
  maxBytes: number;
  allowedMimeTypes: string[];
  allowedExtensions: string[]; // lowercase, with leading dot (e.g. ".pdf")
  label?: string; // used for user-facing error text
};

export type UploadValidationResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | "too_large"
        | "extension_not_allowed"
        | "mime_not_allowed"
        | "signature_mismatch"
        | "executable_blocked"
        | "unreadable";
      message: string;
    };

export function isUploadValidationError(
  res: UploadValidationResult
): res is Extract<UploadValidationResult, { ok: false }> {
  return res.ok === false;
}

const textDecoder = new TextDecoder("ascii", { fatal: false });

function normalizeExtensions(exts: string[]): string[] {
  return (exts ?? [])
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean)
    .map((e) => (e.startsWith(".") ? e : `.${e}`));
}

export function getFileExtension(name: string | undefined | null): string {
  if (!name) return "";
  const base = name.split("/").pop() ?? name;
  const idx = base.lastIndexOf(".");
  if (idx < 0) return "";
  return base.slice(idx).toLowerCase();
}

async function readHeaderBytes(file: File, bytes = 32): Promise<Uint8Array> {
  const blob = file.slice(0, bytes);
  const buf = await blob.arrayBuffer();
  return new Uint8Array(buf);
}

function startsWithBytes(h: Uint8Array, sig: number[]): boolean {
  if (h.length < sig.length) return false;
  for (let i = 0; i < sig.length; i++) {
    if (h[i] !== sig[i]) return false;
  }
  return true;
}

function asciiStartsWith(h: Uint8Array, prefix: string): boolean {
  const s = textDecoder.decode(h);
  return s.startsWith(prefix);
}

function isLikelyExe(h: Uint8Array): boolean {
  // Windows PE/EXE starts with "MZ"
  return h.length >= 2 && h[0] === 0x4d && h[1] === 0x5a;
}

function signatureOkForExtension(ext: string, h: Uint8Array): boolean | null {
  // null => unknown/unsupported signature check (treat as "can't verify")
  switch (ext) {
    case ".pdf":
      return asciiStartsWith(h, "%PDF-");
    case ".png":
      return startsWithBytes(h, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
    case ".jpg":
    case ".jpeg":
      return startsWithBytes(h, [0xff, 0xd8, 0xff]);
    case ".gif":
      return asciiStartsWith(h, "GIF87a") || asciiStartsWith(h, "GIF89a");
    case ".webp":
      // RIFF....WEBP
      return (
        h.length >= 12 &&
        asciiStartsWith(h, "RIFF") &&
        textDecoder.decode(h.slice(8, 12)) === "WEBP"
      );
    case ".mp4":
      // ftyp box usually starts at offset 4
      return h.length >= 12 && textDecoder.decode(h.slice(4, 8)) === "ftyp";
    case ".webm":
      return startsWithBytes(h, [0x1a, 0x45, 0xdf, 0xa3]); // EBML
    case ".mov":
      // quicktime also uses ftyp (brand differs)
      return h.length >= 12 && textDecoder.decode(h.slice(4, 8)) === "ftyp";
    case ".csv":
      // CSV is plain text, no stable signature — just ensure it's not an EXE.
      return !isLikelyExe(h);
    default:
      return null;
  }
}

export async function validateFileForUpload(
  file: File,
  options: UploadValidationOptions
): Promise<UploadValidationResult> {
  const { maxBytes, allowedMimeTypes, allowedExtensions, label } = options;

  if (file.size > maxBytes) {
    return {
      ok: false,
      reason: "too_large",
      message: `File size must be less than ${(maxBytes / (1024 * 1024)).toFixed(0)}MB`,
    };
  }

  const extAllow = normalizeExtensions(allowedExtensions);
  const ext = getFileExtension(file.name);
  if (extAllow.length > 0 && (!ext || !extAllow.includes(ext))) {
    return {
      ok: false,
      reason: "extension_not_allowed",
      message: `Only ${label ?? extAllow.join(", ").toUpperCase()} files are allowed`,
    };
  }

  // MIME from browser is not fully trustworthy, but still a useful first-pass filter.
  if (allowedMimeTypes?.length > 0 && file.type && !allowedMimeTypes.includes(file.type)) {
    return {
      ok: false,
      reason: "mime_not_allowed",
      message: `Only ${label ?? extAllow.join(", ").toUpperCase()} files are allowed`,
    };
  }

  let header: Uint8Array;
  try {
    header = await readHeaderBytes(file, 32);
  } catch {
    return { ok: false, reason: "unreadable", message: "Unable to read file. Please try another file." };
  }

  // Hard block executables even if renamed.
  if (isLikelyExe(header)) {
    return {
      ok: false,
      reason: "executable_blocked",
      message: "Executable files are not allowed",
    };
  }

  // If we can signature-check the extension, enforce it.
  const sigOk = signatureOkForExtension(ext, header);
  if (sigOk === false) {
    return {
      ok: false,
      reason: "signature_mismatch",
      message: "File content does not match the selected file type",
    };
  }

  return { ok: true };
}

export async function antdBeforeUploadAllowList(
  file: File,
  options: UploadValidationOptions,
  onError: (msg: string) => void
): Promise<boolean | typeof Upload.LIST_IGNORE> {
  const res = await validateFileForUpload(file, options);
  if (isUploadValidationError(res)) {
    onError(res.message);
    return Upload.LIST_IGNORE;
  }
  return false; // prevent auto upload; caller handles upload manually
}

